/**
 * ManualLayout — long-scroll document layout for AIP Operating Manuals.
 *
 * Brand locks:
 *   Navy   #0B0B0F
 *   Red    #DC2626
 *   AIP bg #FAFAF7
 *
 * Layout:
 *   - Mi6 brand band (sticky)
 *   - Title block with role + version + PDF link
 *   - Inline ToC, then content column max-w-4xl with sticky right-side ToC on lg+
 *   - Footer back to /#/manuals
 */

import { ReactNode, useEffect, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const RED = "#DC2626";

export interface ManualSection {
  id: string;
  label: string;
}

interface ManualLayoutProps {
  role: string;            // "Marketing"
  meta: string;            // "Marketing role · v1.0 · May 2026"
  title: string;           // e.g. "AIP Operating Manual — Marketing"
  pdfHref?: string;        // download href
  sections: ManualSection[];
  children: ReactNode;
}

export default function ManualLayout({
  role,
  meta,
  title,
  pdfHref,
  sections,
  children,
}: ManualLayoutProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  // Track the section currently in view for ToC active state.
  useEffect(() => {
    const handler = () => {
      // Find the topmost section whose top is above the viewport's mid-line.
      let best = sections[0]?.id ?? "";
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 140) best = s.id;
      }
      setActiveId(best);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [sections]);

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Operating Manuals", href: "/manuals" },
        { label: role },
      ]}
    >
      {/* Title strip */}
      <header
        style={{
          background: "#FFFFFF",
          borderBottom: "1px solid #E5E5E7",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <a
              href="/#/manuals"
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: RED,
                textDecoration: "none",
              }}
            >
              ← Operating Manuals
            </a>
            <h1
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                color: BLACK,
                fontSize: 32,
                fontWeight: 700,
                lineHeight: 1.15,
                margin: "8px 0 6px 0",
              }}
            >
              {title}
            </h1>
            <p
              style={{
                color: "#6B6B70",
                fontSize: 13,
                margin: 0,
              }}
            >
              {meta}
            </p>
          </div>
          {pdfHref && (
            <a
              href={pdfHref}
              download
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: 6,
                border: `1px solid ${BLACK}`,
                color: BLACK,
                background: "#FFFFFF",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
              Download PDF
            </a>
          )}
        </div>
      </header>

      {/* Inline "On this page" + mobile select */}
      <div
        style={{
          background: "#FFFFFF",
          borderBottom: "1px solid #E5E5E7",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="hidden md:flex items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginRight: 6,
              }}
            >
              On this page
            </span>
            {sections.map((s, i) => (
              <a
                key={s.id}
                href={`#/${role.toLowerCase()}-anchor-${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{
                  fontSize: 12,
                  color: BLACK,
                  textDecoration: "none",
                  padding: "3px 8px",
                  borderRadius: 4,
                  background: "transparent",
                }}
              >
                {i + 1}. {s.label}
              </a>
            ))}
          </div>
          {/* Mobile collapsible jump-to */}
          <div className="md:hidden">
            <label
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#6B6B70",
                display: "block",
                marginBottom: 4,
              }}
            >
              Jump to chapter
            </label>
            <select
              onChange={(e) => {
                const id = e.target.value;
                if (id) document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
              }}
              defaultValue=""
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 13,
                border: "1px solid #C9C9CC",
                borderRadius: 6,
                color: BLACK,
                background: "#FFFFFF",
              }}
            >
              <option value="" disabled>Select a chapter</option>
              {sections.map((s, i) => (
                <option key={s.id} value={s.id}>{i + 1}. {s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Body: content + sticky right ToC */}
      <div className="max-w-7xl mx-auto px-6 py-10 lg:flex lg:gap-10 lg:items-start">
        <article
          className="w-full lg:flex-1"
          style={{
            maxWidth: 880,
            margin: "0 auto",
            color: "#0B0B0F",
            fontSize: 16,
            lineHeight: 1.65,
          }}
        >
          {children}

          <hr style={{ margin: "48px 0 24px", border: 0, borderTop: "1px solid #E5E5E7" }} />
          <p style={{ fontSize: 12, color: "#6B6B70" }}>
            Last reviewed: May 2026 · Mi6 Group · Attilius.{" "}
            <a href="/#/manuals" style={{ color: BLACK, fontWeight: 600 }}>
              Back to Operating Manuals
            </a>
            .
          </p>
        </article>

        {/* Sticky right-side ToC (desktop only) */}
        <aside
          className="hidden lg:block"
          style={{
            width: 240,
            flexShrink: 0,
            position: "sticky",
            top: 100,
            alignSelf: "flex-start",
          }}
        >
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 8,
              }}
            >
              Contents
            </div>
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {sections.map((s, i) => {
                const active = s.id === activeId;
                return (
                  <li key={s.id} style={{ marginBottom: 4 }}>
                    <a
                      href={`#/${role.toLowerCase()}-anchor-${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      style={{
                        display: "block",
                        padding: "5px 8px",
                        fontSize: 12,
                        textDecoration: "none",
                        color: active ? BLACK : "#525258",
                        fontWeight: active ? 700 : 500,
                        borderLeft: active ? `3px solid ${RED}` : "3px solid transparent",
                        borderRadius: 3,
                        background: active ? "#FAFAF7" : "transparent",
                        lineHeight: 1.35,
                      }}
                    >
                      {i + 1}. {s.label}
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>
        </aside>
      </div>
    </OperatorShell>
  );
}

// ─── Reusable bits used inside the manual body ────────────────────────────────

const NAVY_COLOR = BLACK;

export function ManualSectionHeading({ id, num, title }: { id: string; num: number; title: string }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        color: NAVY_COLOR,
        fontSize: 26,
        fontWeight: 700,
        marginTop: 56,
        marginBottom: 12,
        scrollMarginTop: 100,
      }}
    >
      <span style={{ color: RED, marginRight: 12, fontSize: 16, fontWeight: 700, letterSpacing: "0.1em" }}>
        CH {num.toString().padStart(2, "0")}
      </span>
      {title}
    </h2>
  );
}

export function Callout({
  tone = "navy",
  children,
}: {
  tone?: "navy" | "red";
  children: ReactNode;
}) {
  const accent = tone === "red" ? RED : NAVY_COLOR;
  return (
    <div
      style={{
        background: "#FAFAF7",
        borderLeft: `4px solid ${accent}`,
        padding: "12px 16px",
        margin: "20px 0",
        borderRadius: 4,
        fontSize: 14,
        color: "#3F3F44",
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

export function ManualImage({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption: string;
}) {
  return (
    <figure style={{ margin: "24px 0" }}>
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          border: `1px solid ${NAVY_COLOR}33`,
          borderRadius: 6,
        }}
      />
      <figcaption
        style={{
          fontSize: 12,
          color: "#6B6B70",
          marginTop: 8,
          fontStyle: "italic",
        }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}

export function StepList({ steps }: { steps: ReactNode[] }) {
  return (
    <ol
      style={{
        margin: "16px 0 16px 0",
        paddingLeft: 24,
        color: "#0B0B0F",
        fontSize: 16,
        lineHeight: 1.7,
      }}
    >
      {steps.map((s, i) => (
        <li key={i} style={{ marginBottom: 8 }}>{s}</li>
      ))}
    </ol>
  );
}

export function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul
      style={{
        margin: "12px 0 12px 0",
        paddingLeft: 24,
        color: "#0B0B0F",
        fontSize: 16,
        lineHeight: 1.7,
      }}
    >
      {items.map((b, i) => (
        <li key={i} style={{ marginBottom: 6 }}>{b}</li>
      ))}
    </ul>
  );
}

export function ManualPara({ children }: { children: ReactNode }) {
  return (
    <p style={{ margin: "12px 0", color: "#0B0B0F", fontSize: 16, lineHeight: 1.7 }}>{children}</p>
  );
}

export function ManualTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div style={{ overflowX: "auto", margin: "16px 0" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontSize: 14,
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <thead>
          <tr style={{ background: "#FAFAF7" }}>
            {headers.map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  color: NAVY_COLOR,
                  fontWeight: 700,
                  borderBottom: "1px solid #E5E5E7",
                  fontSize: 12,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid #EFEFEE" }}>
              {r.map((c, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 14px",
                    color: "#3F3F44",
                    verticalAlign: "top",
                  }}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
